import React, { useMemo, useState } from 'react';
import Box from '@material-ui/core/Box';
import Collapse from '@material-ui/core/Collapse';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import { SectionHeading, Text } from '../../_core/_ui/typography.component';
import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { DashboardCard } from '../../_core/_ui/dashboard-card.component';
import { ParentAddContactButton } from '../../PlayerContact/ParentAddContact/parent-add-contact-button.component';
import { IParentPlayerContact, IParentSelf } from '../../../lib/types';
import { pluralize } from '../../../lib/helpers/text.helpers';
import { messages } from './parent-account.messages';
import { ContactsExpanderHeader } from './parent-account.styles';
import { groupContactsByPlayer } from './parent-player-contacts.utils';
import { ParentPlayerContactEditor } from './parent-player-contact-editor.component';

interface IParentPlayerContactsCardProps {
  parent: IParentSelf;
  contacts: IParentPlayerContact[];
  defaultOpen?: boolean;
  // playerId → player-details path, so each row can deep-link its player.
  playerDetailsPaths: Record<string, string>;
}

// Per-player overrides live behind one expander (default closed); each row is
// then a tight summary that itself expands into editor fields on Edit click.
// Selects pull only from the parent's verified credentials.
export const ParentPlayerContactsCard: React.FC<IParentPlayerContactsCardProps> = ({ parent, contacts, defaultOpen, playerDetailsPaths }) => {
  const [open, setOpen] = useState(!!defaultOpen);
  // A parent can hold more than one row on the same athlete, so the rows are
  // grouped: the count below counts athletes, and each gets one co-contact list.
  const groups = useMemo(() => groupContactsByPlayer(contacts), [contacts]);

  if (contacts.length === 0) {
    return (
      <DashboardCard>
        <SectionHeading><IntlMsg {...messages.perPlayerContactsHeading} /></SectionHeading>
        <Text><IntlMsg {...messages.perPlayerContactsEmpty} /></Text>
      </DashboardCard>
    );
  }

  return (
    <DashboardCard>
      <ContactsExpanderHeader
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls="parent-player-contacts-list"
      >
        <Box>
          <SectionHeading><IntlMsg {...messages.perPlayerContactsHeading} /></SectionHeading>
          <Text>
            {open
              ? <IntlMsg {...messages.perPlayerContactsOpen} />
              : (
                <IntlMsg
                  {...messages.perPlayerContactsClosed}
                  values={{ count: groups.length, playerNoun: pluralize(groups.length, 'player') }}
                />
              )}
          </Text>
        </Box>
        {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </ContactsExpanderHeader>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box id="parent-player-contacts-list" mt={2}>
          {groups.map((group) => (
            <React.Fragment key={group.playerId}>
              {group.contacts.map((contact, index) => (
                <ParentPlayerContactEditor
                  key={contact._id}
                  parent={parent}
                  contact={contact}
                  playerDetailsPath={playerDetailsPaths[contact.playerId]}
                  showOtherContacts={index === 0}
                />
              ))}
            </React.Fragment>
          ))}
          <Box mt={2}>
            <ParentAddContactButton contacts={contacts} />
          </Box>
        </Box>
      </Collapse>
    </DashboardCard>
  );
};
